import type { ScrollbackMessage } from "./api";
import { updateCompose } from "./composeAppend";
import { attributionHead, quotableBody, startsWithReplyQuote } from "./quotableBody";

// #1067 — the reply verb, shared by the left→right swipe on a message row and
// the long-press menu's Reply item. Both doors, one code path.
//
// The quote is built from the MESSAGE, not from the rendered row: the row's
// text also carries the timestamp and the per-message prefix glyph (@/+), and
// scraping those back out is a parser nobody asked for.

// The tail the issue specifies verbatim: `<nick> quoted message << `, spaces on
// both sides — leading so the tail never sits flush against the last word
// (#1235), trailing so the answer is typed straight after the caret.
export const REPLY_QUOTE_TAIL = " << ";

// #1235 — how much of the quoted line comes along. Without a cap, replying to a
// long message drags the whole thing into the compose box and buries the answer.
//
// #1277 raised the number to 100: 42 was the original ask ("limitiamo a 42 i
// caratteri") and read too tight in practice. The four readings ruled on in
// #1235 stand unchanged, and they are still deliberately kept to one constant
// plus one function, which is what made raising the number a one-line edit:
//   - 100 counts BODY characters and the ellipsis is ADDED past them (103
//     quoted, not 100 with three spent on dots);
//   - the marker is the literal `...` the request spells, not `…` U+2026;
//   - the cut is flat at 100, with no backing off to a word boundary;
//   - the head (`<nick> ` / `* nick `) is outside the count — the nick is not
//     what overflows.
export const REPLY_QUOTE_BODY_LIMIT = 100;
export const REPLY_QUOTE_ELLIPSIS = "...";

// The quoted body, cut to the limit. Counted in CODE POINTS: a UTF-16 slice can
// land between the halves of a surrogate pair and put a lone surrogate in the
// compose box, and from there onto the wire.
function capQuotedBody(body: string): string {
  const chars = [...body];
  if (chars.length <= REPLY_QUOTE_BODY_LIMIT) return body;
  return chars.slice(0, REPLY_QUOTE_BODY_LIMIT).join("") + REPLY_QUOTE_ELLIPSIS;
}

// The quote for a message, or null when there is nothing to reply to.
//
// #1107 — the gate and the plain-text extraction moved to `quotableBody`, which
// `addQuoteCommand` now shares. What stays here is the WRAPPER, which is the
// only part reply owns — and that is why the #1235 cap lives here and not in
// the helper: `!addquote` archives the line and must keep it whole.
export function replyQuote(msg: ScrollbackMessage): string | null {
  const body = quotableBody(msg);
  if (body === null) return null;
  // #1126's heads, shared with `!addquote` since #1264 — see `attributionHead`.
  //
  // issue 2033 — `"mention"`: when the row is a bridge relay, the recovered
  // author is emitted as `@nick`, which is what makes the far side notify them
  // (vjt measured that his bridge relays the USERNAME, not the display name).
  // The cap then falls on a body that has already shed the relay head, because
  // `quotableBody` took it off — ruling 5, and the same reading #1235 made for
  // the nick: the head is not what overflows.
  return `${attributionHead(msg, "mention")} ${capQuotedBody(body)}${REPLY_QUOTE_TAIL}`;
}

// The marker and its trailing space — the part of the tail a second reply takes
// back. The LEADING space stays: #1235 put it there so the tail never sits
// flush against the last word, and it is exactly the one space the next quote
// needs in front of it. Derived from the tail rather than spelled again, so the
// two cannot drift.
const REPLY_QUOTE_MARKER = REPLY_QUOTE_TAIL.trimStart();

// #1357 — what the draft must look like BEFORE this reply's quote is appended.
// vjt's ruling (2026-08-15): the line accumulates, the marker does not repeat,
// so a draft that ENDS with our tail sheds the marker and the new quote brings
// the only one — `<a> primo <b> secondo << `.
//
// Only when the tail is at the END. Once the operator has typed their answer
// the tail is mid-line, and cutting it there rewrites a human's sentence to fix
// our own duplication; that line keeps two markers on purpose, because the
// alternative is mangling text somebody wrote.
//
// THIS is the function to change if replace-semantics ever wins (vjt: "start
// with 2, we change it later if needed") — last-swipe-wins means returning the
// draft with its whole quote removed instead of just the marker.
// `draftWithReplyQuote` below hands it the draft and appends to whatever comes
// back, so nothing else moves.
export function draftBeforeReplyQuote(draft: string): string {
  if (!draft.endsWith(REPLY_QUOTE_TAIL)) return draft;
  return draft.slice(0, draft.length - REPLY_QUOTE_MARKER.length);
}

// #1688 — WHERE the quote goes. The tail is documented above as ending the line
// "so the answer is typed straight after the caret": the shape is
// `quote << answer`, an invariant about the END of the line and not merely
// about concatenation. A draft holding the operator's own words used to come
// back with the quote BEHIND them, inverting it — peluche reported the result
// on #grappa, and the `<<` then reads as if it separated a quote from an answer
// sitting in front of it.
//
// So: a draft that already carries a quote of OURS is extended (the #1357
// path); anything else is the operator's text and the quote goes in FRONT of
// it, restoring the documented order.
//
// "Carries a quote of ours" is deliberately two tests, not one.
//   - ENDS with our tail — the #1357 accumulate case, whose marker is shed.
//   - STARTS with our quote shape (`startsWithReplyQuote`) — the case where the
//     operator has already typed an answer past the tail. #1357's acceptance
//     criterion 3 rules on exactly that draft: the new quote "must not land
//     before that text". A trigger phrased as "does not end with the tail"
//     — which is how the shapes were first written down — would prepend there
//     and break a standing ruling, so the head test is what keeps the two
//     issues compatible.
// Neither test is a bare `<<` search: `shift << 2` is somebody's sentence, and
// treating it as ours is how #1688 would stay unfixed for the drafts that
// happen to contain the marker.
//
// The caret needs no decision here, measured rather than assumed:
// `updateCompose` places it at the very end unconditionally, which after a
// prepend IS the end of the typed answer.
export function draftWithReplyQuote(draft: string, quote: string): string {
  if (draft.endsWith(REPLY_QUOTE_TAIL) || startsWithReplyQuote(draft)) {
    return draftBeforeReplyQuote(draft) + quote;
  }
  return quote + draft;
}

// Drop the quote into the window's compose box with the caret at the end. A
// no-op for a row with nothing to quote — the gesture still slid and snapped
// back, which is the honest feedback for "armed, but this row has no reply".
export function replyToMessage(
  msg: ScrollbackMessage,
  networkSlug: string,
  channelName: string,
): void {
  const quote = replyQuote(msg);
  if (quote === null) return;
  updateCompose(networkSlug, channelName, (draft) => draftWithReplyQuote(draft, quote));
}
